// /resources/js/app/modules/v2/app/V2ActionHandler.js
import { v2DataService } from '../V2DataService.js';
import { v2Renderer } from '../V2Renderer.js';
import { v2LiveUpdater } from '../V2LiveUpdater.js';
import { apiClient } from '../apiClient.js';
import { v2StateManager } from './V2StateManager.js';
import { v2UIManager } from './V2UIManager.js';

/**
 * @class V2ActionHandler
 * The core orchestrator for the V2 application. It contains the application's
 * primary workflows, such as initializing the app, loading streams, and handling
 * background polling. It interacts with data services and the renderer to execute
 * tasks requested by the UIManager.
 */
class V2ActionHandler {
    constructor() {
        /**
         * @property {number|null} statusPollInterval - Holds the ID from setInterval for the main status poll.
         */
        this.statusPollInterval = null;
        /**
         * @property {number|null} nextUpdateTimerInterval - Holds the ID from setInterval for the visual countdown timer.
         */
        this.nextUpdateTimerInterval = null;
        /**
         * @property {number} lastSuccessfulCheckTimestamp - Timestamp of the last successful status API call. Used for smart polling.
         */
        this.lastSuccessfulCheckTimestamp = 0;
    }

    /**
     * The main entry point for the application. It performs the initial setup,
     * fetches the stream definitions, and loads the default stream.
     */
    async init() {
        // Perform a Sanctum handshake for authenticated users to ensure API requests are cookied correctly.
        if (window.SGNL.user && window.SGNL.user.id) {
            try {
                await apiClient.get('/sanctum/csrf-cookie');
            } catch (error) {
                console.error('[V2ActionHandler] Sanctum handshake failed.', error);
            }
        }

        // Initialize the live price updater, passing a reference to this handler.
        v2LiveUpdater.init(this);

        try {
            // Fetch the stream definitions (e.g., "Trending") and render the top navigation.
            v2StateManager.streams = await v2DataService.fetchStreamDefinitions();
            v2StateManager.streams.forEach(stream => {
                if (stream.total_signals !== undefined) {
                    v2StateManager.streamTotalCounts[stream.slug] = stream.total_signals;
                }
            });
            v2Renderer.renderStreamNavigator(v2StateManager.streams, window.SGNL.user);
            v2Renderer.renderAllStreamTotalCounts(v2StateManager.streamTotalCounts);
            
            // Determine the initial stream to display. Default to "My Signals" for logged-in users.
            v2StateManager.activeStreamSlug = (window.SGNL.user && window.SGNL.user.id) ? 'my-signals' : (v2StateManager.streams[0]?.slug || null);

            // If a stream is available, load its content. Otherwise, show an empty state.
            if (v2StateManager.activeStreamSlug) {
                await this.loadActiveStream();
            } else {
                v2Renderer.renderEmptyState('No streams available.');
            }
            // Kick off the background process to check for new signals.
            this.startStatusPolling();
        } catch (error) {
            v2Renderer.renderErrorState('Failed to initialize the application.');
        }
    }

    /**
     * Fetches and renders data for the currently active stream. This is the main
     * data loading workflow for the application.
     * @param {number} [page=1] - The page number to fetch.
     */
    async loadActiveStream(page = 1) {
        if (v2StateManager.isLoading) return;
        v2StateManager.isLoading = true;
        
        const previousStreamSlug = v2StateManager.activeStreamSlug;
        this.clearStreamNotifications(v2StateManager.activeStreamSlug);
        this._updatePageTitle(); // <-- ADD THIS LINE
        v2Renderer.hideRefreshBanner();

        // Show the appropriate loading state.
        v2StateManager.isInitialLoad ? v2Renderer.renderLoadingState() : v2Renderer.updateStatusDisplay({ text: 'Loading stream...', isLoading: true });
        v2Renderer.updateActiveStream(v2StateManager.activeStreamSlug);

        // --- START: MODIFIED CONTEXT HEADER LOGIC ---
        // Render the context header immediately, before the data loads.
        const activeStreamDef = v2StateManager.streams.find(s => s.slug === v2StateManager.activeStreamSlug);
        if (v2StateManager.activeStreamSlug === 'my-signals') {
             v2Renderer.renderStreamContext({ name: 'My Signals', description: 'Signals you pioneered or contributed to.' });
        } else {
            v2Renderer.renderStreamContext(activeStreamDef);
        }
        // --- END: MODIFIED CONTEXT HEADER LOGIC ---

        try {
            // Guard Clause: Prevent API calls for "My Signals" if the user isn't fully connected.
            if (v2StateManager.activeStreamSlug === 'my-signals' && !v2UIManager._isTelegramConnected()) {
                v2StateManager.signals = [];
                v2StateManager.pagination = {};
                v2Renderer.renderMySignalsEmptyState(false);
                v2LiveUpdater.stopPolling();
                return;
            }

            // Clear optimistic signals when switching to/from the "My Signals" tab.
            if (previousStreamSlug === 'my-signals' || v2StateManager.activeStreamSlug === 'my-signals') {
                v2StateManager.optimisticSignals = [];
            }
            
            // Fetch the data from the API, passing all current state parameters.
            const data = await v2DataService.fetchStream(v2StateManager.activeStreamSlug, page, v2StateManager.sort.by, v2StateManager.sort.dir, true, v2StateManager.showUnlockedOnly);
            
            // Update total for the current stream from pagination data to keep it fresh
            v2StateManager.streamTotalCounts[v2StateManager.activeStreamSlug] = data.total;
            v2Renderer.renderAllStreamTotalCounts(v2StateManager.streamTotalCounts);
            
            v2StateManager.signals = data.data;
            v2StateManager.pagination = data;
            
            // Render the appropriate content based on the API response.
            if (v2StateManager.activeStreamSlug === 'my-signals' && v2StateManager.signals.length === 0 && v2StateManager.optimisticSignals.length === 0) {
                v2Renderer.renderMySignalsEmptyState(true);
            } else {
                this.renderCurrentView();
            }
            
            v2LiveUpdater.startPolling();
            if (v2StateManager.isInitialLoad) v2StateManager.isInitialLoad = false;

        } catch (error) {
            console.error(`[V2ActionHandler] Failed to load stream '${v2StateManager.activeStreamSlug}'.`, error);
            v2Renderer.renderErrorState('Could not load the requested stream.');
            v2LiveUpdater.stopPolling();
        } finally {
            v2StateManager.isLoading = false;
        }
    }

    /**
     * Renders the current view (card or table) based on the state. It correctly
     * merges optimistic signals for the "My Signals" stream before rendering.
     */
    renderCurrentView() {
        const isMySignalsStream = v2StateManager.activeStreamSlug === 'my-signals';
        const signalsToRender = isMySignalsStream
            ? [...v2StateManager.optimisticSignals, ...v2StateManager.signals] 
            : v2StateManager.signals;

        v2StateManager.viewMode === 'card' 
            ? v2Renderer.renderCardView(signalsToRender, v2StateManager.sort, isMySignalsStream)
            : v2Renderer.renderTableView(signalsToRender, v2StateManager.sort);
        
        v2Renderer.renderPagination(v2StateManager.pagination, (newPage) => this.loadActiveStream(newPage));
    }
    _updatePageTitle() {
        const totalNew = Object.values(v2StateManager.streamUpdateCounts).reduce((sum, count) => sum + (count || 0), 0);
        
        if (totalNew > 0) {
            document.title = `(${totalNew}) ${v2StateManager.originalPageTitle}`;
        } else {
            document.title = v2StateManager.originalPageTitle;
        }
    }
    /**
     * Handles the logic for changing to a new stream.
     * @param {string} newSlug - The slug of the stream to switch to.
     */
    changeStream(newSlug) {
        v2StateManager.activeStreamSlug = newSlug;
        // Reset sorting and filters to their defaults for the new stream.
        
        // Find the full definition for the newly selected stream.
        const activeStreamDef = v2StateManager.streams.find(s => s.slug === newSlug);

        // Set the sort state from the stream's definition, with a safe fallback.
        if (activeStreamDef && activeStreamDef.sort) {
            v2StateManager.sort = { by: activeStreamDef.sort.by, dir: activeStreamDef.sort.dir };
        } else {
            // Fallback for any stream that might be missing a sort definition.
            v2StateManager.sort = { by: 'call_timestamp', dir: 'desc' };
        }
        v2StateManager.viewMode = activeStreamDef?.default_view || 'card';
        v2StateManager.showUnlockedOnly = false;
        console.log(`[V2ActionHandler] 'showUnlockedOnly' state reset to:`, v2StateManager.showUnlockedOnly);

        // This event tells Alpine to re-read the state from the StateManager.
        window.dispatchEvent(new CustomEvent('state-change'));
        console.log(`[V2ActionHandler] Dispatched 'state-change' event.`);
        this.loadActiveStream(1);
    }
    
    /**
     * Handles the logic for changing the sort order.
     * @param {string} sortBy - The key to sort by.
     */
    changeSort(sortBy) {
        if (v2StateManager.sort.by === sortBy) {
            // If clicking the same column, toggle the direction.
            v2StateManager.sort.dir = v2StateManager.sort.dir === 'desc' ? 'asc' : 'desc';
        } else {
            // If clicking a new column, set it and use its default direction.
            v2StateManager.sort.by = sortBy;
            const sortTrigger = document.querySelector(`[data-sort-by="${sortBy}"]`);
            v2StateManager.sort.dir = sortTrigger?.dataset.defaultDir || 'desc';
        }
        this.loadActiveStream(1);
    }
    
    /**
     * Handles changing the view mode between 'card' and 'table'.
     * @param {string} newMode - The new view mode.
     */
    changeViewMode(newMode) {
        v2StateManager.viewMode = newMode;
        localStorage.setItem('v2_view_mode', newMode);
        v2Renderer.updateViewSwitcher(newMode);
        // Re-rendering is a cheap operation, no need for a full network reload.
        this.renderCurrentView();
    }
    
    
    /**
     * Starts the background polling to check for new signals.
     */
    startStatusPolling() {
        this.stopStatusPolling();
        const pollInterval = 30000;
        const timeSinceLastCheck = Date.now() - this.lastSuccessfulCheckTimestamp;
        // Smart polling: if the tab was just re-focused and data is stale, check immediately.
        if (timeSinceLastCheck >= pollInterval) this._checkForUpdates();
        // Otherwise, schedule the check for the remaining time.
        else setTimeout(() => this._checkForUpdates(), pollInterval - timeSinceLastCheck);
        this.statusPollInterval = setInterval(() => this._checkForUpdates(), pollInterval);
        this.startVisualTimeAgoTimer(pollInterval);
    }

    /**
     * Stops the background polling.
     */
    stopStatusPolling() {
        clearInterval(this.statusPollInterval);
        clearInterval(this.nextUpdateTimerInterval);
    }

    /**
     * --- START: MODIFIED METHOD ---
     * Starts the visual "time ago" timer in the UI.
     * @param {number} duration - The duration of the countdown in milliseconds.
     */
    startVisualTimeAgoTimer(duration) {
        clearInterval(this.nextUpdateTimerInterval);

        // This is the timestamp when this 30-second cycle began.
        const cycleStartTime = Date.now();
        
        const update = () => {
            if (v2StateManager.isLoading) return;

            // Calculate how many seconds have passed since this cycle started.
            const secondsElapsed = Math.round((Date.now() - cycleStartTime) / 1000);
            
            // Display the count-up text.
            v2Renderer.updateStatusDisplay({ text: 'Last updated:', timer: `${secondsElapsed}s ago` });

            // If we've counted past the duration, it means an update is happening.
            if (secondsElapsed > (duration / 1000)) {
                v2Renderer.updateStatusDisplay({ text: 'Checking for updates...', isLoading: true });
                clearInterval(this.nextUpdateTimerInterval);
            }
        };

        // Immediately show "0s ago" then start the 1-second interval.
        update(); 
        this.nextUpdateTimerInterval = setInterval(update, 1000);
    }

    /**
     * Performs a single API call to the status endpoint to check for new data.
     */
    async _checkForUpdates() {
        if (v2StateManager.isLoading) return;
        try {
            const statusResponse = await v2DataService.fetchStatus();
            this.lastSuccessfulCheckTimestamp = Date.now();            
            v2Renderer.updateStatusDisplay({ text: 'Last checked:', timer: `now` });

            const newToken = statusResponse?.last_updated_token;

            if (v2StateManager.isInitialLoad) {
                v2StateManager.lastStatusToken = newToken;
                return;
            }
            // If the token has changed, it means new signals are available.
            if (newToken && newToken !== v2StateManager.lastStatusToken) {
                v2StateManager.lastStatusToken = newToken;
                this.handleNewDataAvailable(statusResponse.update_counts || {});
            }
        } catch (error) {
            console.warn("Status check failed:", error);
        } finally {
            // Restart the visual timer for the next cycle.
            this.startVisualTimeAgoTimer(30000);
        }
    }
    
    /**
     * Handles the logic when the status poll detects new data.
     * @param {object} newCounts - An object mapping stream slugs to new signal counts.
     */
    handleNewDataAvailable(newCounts) {
        // Aggregate the new counts with any existing counts.
        for (const [slug, count] of Object.entries(newCounts)) {
            v2StateManager.streamUpdateCounts[slug] = (v2StateManager.streamUpdateCounts[slug] || 0) + count;
        }

        this.updateAllStreamBadges();
        this._updatePageTitle();

        // If there are new signals for the *currently active* stream, show the refresh banner.
        const newSignalsForActiveStream = v2StateManager.streamUpdateCounts[v2StateManager.activeStreamSlug] || 0;
        if (newSignalsForActiveStream > 0) {
            v2Renderer.showRefreshBanner(newSignalsForActiveStream);
        }
    }
    
    /**
     * Updates the notification badges on all non-active stream tabs.
     */
    updateAllStreamBadges() {
        const allSlugs = ['my-signals', ...v2StateManager.streams.map(s => s.slug)];
        allSlugs.forEach(slug => {
            // Only show badges on tabs that are NOT the one the user is currently looking at.
            if (slug !== v2StateManager.activeStreamSlug) {
                v2Renderer.updateStreamTabBadge(slug, v2StateManager.streamUpdateCounts[slug] || 0);
            }
        });
    }

    /**
     * Clears the notification count for a given stream, typically when the user clicks on it.
     * @param {string} slug - The slug of the stream to clear.
     */
    clearStreamNotifications(slug) {
        if (v2StateManager.streamUpdateCounts[slug]) {
            v2StateManager.streamUpdateCounts[slug] = 0;
            v2Renderer.updateStreamTabBadge(slug, 0);
        }
    }

    /**
     * A helper method for the live updater to get a list of signals currently visible on screen.
     * @returns {Array} - A filtered array of unlocked signal objects.
     */
    getVisibleUnlockedSignals() {
        const allVisible = v2StateManager.activeStreamSlug === 'my-signals' ?
            [...v2StateManager.optimisticSignals, ...v2StateManager.signals] :
            v2StateManager.signals;
        return allVisible.filter(s => s.lock_status === 'unlocked');
    }
}

export const v2ActionHandler = new V2ActionHandler();